import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertCircle, ArrowLeft, Award, Briefcase, Building2, Check, CheckCircle2, Download,
  ExternalLink, FileText, Loader2, Mail, MapPin, MessageCircle, Phone, Send, X,
} from 'lucide-react';

import { adminApi } from '../services/api.jsx';
import { anciennete, CLASSE_STATUT } from '../components/Candidatures';
import {
  annoncerSucces, BarreProgression, EtatErreur, Squelette, SqueletteCarte, SqueletteImage,
} from '../etats';

/* ═══════════════════════════════════════════════════════════════════════════
   LE DOSSIER D'UNE CANDIDATURE

   Tout est visible sans cliquer : on instruit un dossier, on ne le fouille
   pas.

     ┌ identité, contacts, ancienneté ─┬─ motivation, expertise en bref ┐
     ├──────────── domaines d'expertise, un par pastille ───────────────┤
     ├──────────── le CV, affiché ───────────────┬─ grade · décision ───┤
     └───────────────────────────────────────────┴─ message · notes ────┘

   La décision reste le seul geste qui crée un compte. Le refus exige un
   motif, repris tel quel dans l'e-mail ; les notes internes ne sortent
   jamais de l'équipe.
   ═══════════════════════════════════════════════════════════════════════════ */

function Info({ icone: Icone, libelle, children }) {
  if (!children) return null;
  return (
    <div className="flex items-start gap-3">
      <span className="w-8 h-8 rounded-pastille bg-[var(--bg-main)] grid place-items-center shrink-0">
        <Icone className="w-4 h-4 text-[var(--text-muted)]" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="surtitre text-[var(--text-muted)]">{libelle}</p>
        <div className="text-[13.5px] text-[var(--text-bold)] break-words">{children}</div>
      </div>
    </div>
  );
}

function Titre({ children, compte }) {
  return (
    <h2 className="surtitre text-[var(--text-muted)] mb-3 flex items-center gap-2">
      {children}
      {compte != null && <span className="etiquette etiq-neutre text-[11px]">{compte}</span>}
    </h2>
  );
}

function lienWhatsApp(numero) {
  const chiffres = (numero || '').replace(/[^\d]/g, '');
  return chiffres ? `https://wa.me/${chiffres}` : null;
}

/* ── Le CV, affiché dans la page ───────────────────────────────────────── */
function VueCv({ candidature }) {
  const [etat, setEtat] = useState({ chargement: true });

  useEffect(() => {
    if (!candidature.a_un_cv) { setEtat({ absent: true }); return undefined; }
    let url = null;
    let annule = false;
    adminApi.cvCandidature(candidature.id)
      .then((brut) => {
        // Le type est reposé ici : le dépôt n'accepte qu'un PDF vérifié.
        url = URL.createObjectURL(new Blob([brut], { type: 'application/pdf' }));
        if (annule) URL.revokeObjectURL(url); else setEtat({ url });
      })
      .catch((e) => { if (!annule) setEtat({ erreur: e?.message || 'Le CV n’a pas pu être chargé.' }); });
    return () => { annule = true; if (url) URL.revokeObjectURL(url); };
  }, [candidature.id, candidature.a_un_cv]);

  const nom = `CV ${candidature.nom_complet}.pdf`;
  return (
    <section className="bg-[var(--bg-card)] rounded-carte shadow-carte overflow-hidden flex flex-col">
      <header className="flex items-center gap-3 px-5 py-3 border-b border-[var(--border)]">
        <span className="w-9 h-9 rounded-pastille grid place-items-center bg-[var(--alerte-fond)] text-[var(--alerte)] shrink-0">
          <FileText className="w-4 h-4" />
        </span>
        <p className="font-semibold text-[var(--text-bold)] flex-1 min-w-0 truncate">Curriculum vitæ</p>
        {etat.url && (
          <div className="flex items-center gap-1">
            <a href={etat.url} target="_blank" rel="noreferrer" className="act act-icone act-sm act-tertiaire"
               title="Ouvrir dans un nouvel onglet" aria-label="Ouvrir dans un nouvel onglet">
              <ExternalLink className="w-4 h-4" />
            </a>
            <a href={etat.url} download={nom} className="act act-icone act-sm act-tertiaire"
               title="Télécharger" aria-label="Télécharger le CV">
              <Download className="w-4 h-4" />
            </a>
          </div>
        )}
      </header>
      <div className="bg-[var(--bg-main)] h-[78vh] min-h-[480px]">
        {etat.chargement ? (
          <div className="h-full grid place-items-center p-6">
            <div className="w-full max-w-xs"><BarreProgression libelle="Préparation de l’aperçu du CV…" /></div>
          </div>
        ) : etat.absent ? (
          <div className="h-full grid place-items-center text-[13px] text-[var(--text-muted)]">Aucun CV joint à cette candidature.</div>
        ) : etat.erreur ? (
          <div className="h-full grid place-items-center p-6">
            <div className="encart encart-alerte max-w-md"><AlertCircle className="w-4 h-4" /><p>{etat.erreur}</p></div>
          </div>
        ) : (
          <iframe title={nom} src={etat.url} className="w-full h-full border-0 bg-white" />
        )}
      </div>
    </section>
  );
}

/* ── Le grade que donne l'ancienneté ───────────────────────────────────── */
function Grade({ candidature }) {
  const g = candidature.grade_attendu_detail;
  const fige = candidature.statut === 'acceptee' && candidature.grade_libelle;
  return (
    <section className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5">
      <Titre>Grade</Titre>
      {!g ? (
        <p className="text-[13px] text-[var(--text-muted)]">
          Le profil « {candidature.profil_libelle} » ne reçoit pas de grade.
        </p>
      ) : !g.libelle ? (
        <p className="encart encart-attention text-[13px]">
          Aucun grade du barème ne couvre {anciennete(candidature.annees_experience).toLowerCase()} d’expérience.
          Complétez le barème avant de décider.
        </p>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-pastille grid place-items-center bg-[var(--vert-pale)] text-[var(--vert-fonce)] shrink-0">
              <Award className="w-6 h-6" />
            </span>
            <div>
              <p className="text-[18px] font-semibold text-[var(--text-bold)] leading-tight">{fige ? candidature.grade_libelle : g.libelle}</p>
              <p className="text-[12.5px] text-[var(--text-muted)]">
                {fige ? 'Attribué à l’acceptation' : `${anciennete(candidature.annees_experience)} d’expérience · dès ${g.annees_minimum} an${g.annees_minimum > 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          {g.description && <p className="text-[12.5px] text-[var(--text-main)] mt-3">{g.description}</p>}
          {g.bareme?.length > 1 && (
            <ol className="mt-4 space-y-1.5">
              {g.bareme.map((niveau) => {
                const actif = niveau.code === g.code;
                return (
                  <li key={niveau.code}
                      className={`flex items-center justify-between rounded-pastille px-3 py-1.5 text-[12.5px] ${
                        actif ? 'bg-[var(--vert-pale)] text-[var(--vert-fonce)] font-semibold' : 'text-[var(--text-muted)]'}`}>
                    <span className="flex items-center gap-1.5">{actif && <Check className="w-3.5 h-3.5" />}{niveau.libelle}</span>
                    <span className="tabular-nums">dès {niveau.annees_minimum} an{niveau.annees_minimum > 1 ? 's' : ''}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </>
      )}
    </section>
  );
}

/* ── La décision ───────────────────────────────────────────────────────── */
function Decision({ candidature, peutDecider, onMaj }) {
  const [choix, setChoix] = useState(null);   // 'accepter' | 'refuser'
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  if (['acceptee', 'refusee'].includes(candidature.statut)) {
    const acceptee = candidature.statut === 'acceptee';
    return (
      <section className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5">
        <Titre>Décision</Titre>
        <p className={`encart ${acceptee ? 'encart-ok' : 'encart-alerte'} text-[13px]`}>
          {acceptee ? <CheckCircle2 className="w-4 h-4" /> : <X className="w-4 h-4" />}
          <span>{acceptee ? 'Candidature acceptée' : 'Candidature non retenue'}</span>
        </p>
        <p className="text-[12.5px] text-[var(--text-muted)] mt-3">
          Par {candidature.decide_par_nom || 'un administrateur'}
          {candidature.date_decision && ` le ${new Date(candidature.date_decision).toLocaleDateString('fr-FR')}`}
          {candidature.compte_username && ` · compte : ${candidature.compte_username}`}
        </p>
        {candidature.motif_decision && (
          <p className="text-[13px] text-[var(--text-main)] mt-2 whitespace-pre-line">« {candidature.motif_decision} »</p>
        )}
      </section>
    );
  }

  const trancher = async () => {
    if (choix === 'refuser' && !motif.trim()) return;
    setEnvoi(true);
    setErreur('');
    try {
      const maj = await adminApi.deciderCandidature(candidature.id, choix, motif.trim());
      onMaj(maj);
      annoncerSucces(choix === 'accepter'
        ? 'Candidature acceptée : le compte est créé et le lien d’activation envoyé.'
        : 'Candidature non retenue : le candidat est prévenu par e-mail.');
      setChoix(null);
      setMotif('');
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <section className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5">
      <Titre>Décision</Titre>
      {!peutDecider ? (
        <p className="text-[13px] text-[var(--text-muted)]">La décision est réservée aux administrateurs.</p>
      ) : !choix ? (
        <div className="grid gap-2">
          <button type="button" onClick={() => setChoix('accepter')} className="act act-sm">
            <Check className="w-4 h-4" /> Accepter
          </button>
          <button type="button" onClick={() => setChoix('refuser')} className="act act-sm act-danger">
            <X className="w-4 h-4" /> Ne pas retenir
          </button>
        </div>
      ) : (
        <div>
          <p className="text-[13.5px] font-semibold text-[var(--text-bold)]">
            {choix === 'accepter' ? 'Accepter cette candidature' : 'Ne pas retenir cette candidature'}
          </p>
          <p className="text-[12.5px] text-[var(--text-muted)] mt-1 mb-3">
            {choix === 'accepter'
              ? 'Un compte est créé et un lien de première connexion envoyé : la personne choisira son mot de passe.'
              : 'Ce motif est repris tel quel dans l’e-mail envoyé au candidat.'}
          </p>
          <textarea rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} className="champ text-[13px]"
                    placeholder={choix === 'accepter' ? 'Message d’accueil (facultatif)' : 'Motif communiqué au candidat (obligatoire)'} />
          {erreur && <p className="text-[12px] text-[var(--alerte)] mt-2">{erreur}</p>}
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button type="button" onClick={() => { setChoix(null); setMotif(''); }} className="act act-sm act-contour">Annuler</button>
            <button type="button" onClick={trancher} disabled={envoi || (choix === 'refuser' && !motif.trim())}
                    className={`act act-sm ${choix === 'refuser' ? 'act-danger-plein' : ''}`}>
              {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Confirmer
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/* ── Écrire au candidat ────────────────────────────────────────────────── */
function Messages({ candidature, peutEcrire, onMaj }) {
  const [ouvert, setOuvert] = useState(false);
  const [sujet, setSujet] = useState('');
  const [texte, setTexte] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [retour, setRetour] = useState(null);
  const messages = candidature.messages || [];

  const envoyer = async (e) => {
    e.preventDefault();
    if (!texte.trim()) return;
    setEnvoi(true);
    setRetour(null);
    try {
      const maj = await adminApi.ecrireCandidat(candidature.id, sujet.trim(), texte.trim());
      onMaj(maj);
      setSujet('');
      setTexte('');
      setOuvert(false);
      setRetour(null);
      annoncerSucces(`Message envoyé à ${candidature.email}.`);
    } catch (err) {
      setRetour({ ton: 'alerte', texte: err.message });
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <section className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5">
      <Titre compte={messages.length || null}>Messages au candidat</Titre>
      {retour && <p className={`encart encart-${retour.ton} text-[12.5px] mb-3`}>{retour.texte}</p>}
      {peutEcrire && !ouvert && (
        <button type="button" onClick={() => setOuvert(true)} className="act act-sm act-contour w-full">
          <MessageCircle className="w-4 h-4" /> Écrire au candidat
        </button>
      )}
      {ouvert && (
        <form onSubmit={envoyer} className="space-y-2">
          <input className="champ text-[13px]" value={sujet} maxLength={200} onChange={(e) => setSujet(e.target.value)}
                 placeholder="Objet (par défaut : Votre candidature ASSO-PRO)" />
          <textarea className="champ text-[13px]" rows={5} value={texte} maxLength={5000} required
                    onChange={(e) => setTexte(e.target.value)}
                    placeholder="Une pièce à compléter, une question, une date d’entretien…" />
          <p className="text-[11.5px] text-[var(--text-muted)]">Envoyé par e-mail à {candidature.email}. Le message reste au dossier.</p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setOuvert(false)} className="act act-sm act-contour">Annuler</button>
            <button type="submit" disabled={envoi || !texte.trim()} className="act act-sm">
              {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer
            </button>
          </div>
        </form>
      )}
      {messages.length > 0 && (
        <ul className="mt-4 space-y-3">
          {messages.map((m) => (
            <li key={m.id} className="rounded-pastille border border-[var(--border)] p-3">
              <p className="text-[13px] font-semibold text-[var(--text-bold)]">{m.sujet}</p>
              <p className="text-[11.5px] text-[var(--text-muted)]">
                {m.auteur_nom || 'L’équipe'} · {new Date(m.envoye_le).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                {!m.remis && ' · non parti'}
              </p>
              <p className="text-[12.5px] text-[var(--text-main)] mt-1.5 whitespace-pre-line line-clamp-4">{m.corps}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ── Notes internes ────────────────────────────────────────────────────── */
function Notes({ candidature, onMaj }) {
  const [texte, setTexte] = useState(candidature.notes_internes || '');
  const [etat, setEtat] = useState('');
  useEffect(() => { setTexte(candidature.notes_internes || ''); }, [candidature.notes_internes]);
  const modifie = texte !== (candidature.notes_internes || '');

  const enregistrer = async () => {
    setEtat('envoi');
    try {
      onMaj(await adminApi.annoterCandidature(candidature.id, { notes_internes: texte }));
      setEtat('ok');
    } catch {
      setEtat('erreur');
    }
  };

  return (
    <section className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5">
      <Titre>Notes internes</Titre>
      <textarea className="champ text-[13px]" rows={3} value={texte} onChange={(e) => { setTexte(e.target.value); setEtat(''); }}
                placeholder="Jamais communiquées au candidat." />
      <div className="flex items-center justify-between gap-2 mt-2">
        <span className="text-[11.5px] text-[var(--text-muted)]">
          {etat === 'ok' ? 'Enregistré.' : etat === 'erreur' ? 'Non enregistré.' : 'Visibles par l’équipe seulement.'}
        </span>
        <button type="button" onClick={enregistrer} disabled={!modifie || etat === 'envoi'} className="act act-sm act-contour">
          Enregistrer
        </button>
      </div>
    </section>
  );
}

export default function CandidatureDetail({ user }) {
  const { id } = useParams();
  const [candidature, setCandidature] = useState(null);
  const [erreur, setErreur] = useState('');
  const peutDecider = ['SUPER_ADMIN', 'ADMIN'].includes(user?.role);

  const charger = useCallback(async () => {
    setErreur('');
    try {
      setCandidature(await adminApi.getCandidature(id));
    } catch (e) {
      setErreur(e);
    }
  }, [id]);
  useEffect(() => { charger(); }, [charger]);

  const retour = (
    <Link to="/coaches?onglet=candidatures" className="act act-tertiaire act-sm -ml-2 mb-4">
      <ArrowLeft className="w-4 h-4" /> Candidatures
    </Link>
  );

  if (erreur && !candidature) {
    return (
      <div className="p-1">{retour}
        <div className="bg-[var(--bg-card)] rounded-carte shadow-carte"><EtatErreur erreur={erreur} onReessayer={charger} /></div>
      </div>
    );
  }
  if (!candidature) {
    // La forme du dossier : en-tête, bandeau en deux colonnes, CV | colonne.
    return (
      <div className="space-y-5" role="status" aria-label="Chargement du dossier">
        <div><Squelette className="h-3 w-28 mb-5" /><Squelette className="h-8 w-72 mb-2" /><Squelette className="h-3 w-56" /></div>
        <div className="grid md:grid-cols-2 gap-5"><SqueletteCarte /><SqueletteCarte /></div>
        <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6 flex gap-2 flex-wrap">
          {[120, 160, 140, 110, 150].map((l) => <Squelette key={l} className="h-9 !rounded-full" style={{ width: l }} />)}
        </div>
        <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-5">
          <SqueletteImage className="h-[60vh] w-full" />
          <div className="space-y-5"><SqueletteCarte /><SqueletteCarte /></div>
        </div>
      </div>
    );
  }

  const c = candidature;
  const contacts = (c.contacts?.length ? c.contacts : (c.telephone ? [{ numero: c.telephone }] : []))
    .filter((t) => t?.numero);
  const domaines = c.domaines_detail || [];

  return (
    <div className="space-y-5">
      <div>
        {retour}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="titre-page">{c.nom_complet}</h1>
            <p className="text-[13.5px] text-[var(--text-muted)] mt-1">
              Candidature {c.profil_libelle.toLowerCase()} · déposée le {new Date(c.date_depot).toLocaleDateString('fr-FR', { dateStyle: 'long' })}
            </p>
          </div>
          <span className={`etiquette ${CLASSE_STATUT[c.statut] || 'etiq-neutre'}`}>{c.statut_libelle}</span>
        </div>
      </div>

      {/* ── Bandeau : la personne | ce qu'elle veut faire ─────────────── */}
      <section className="bg-[var(--bg-card)] rounded-carte shadow-carte grid md:grid-cols-2 md:divide-x divide-[var(--border)]">
        <div className="p-6">
          <Titre>Informations personnelles</Titre>
          <div className="flex items-center gap-4 mb-5 rounded-pastille bg-[var(--bg-main)] p-4">
            <p className="text-[34px] leading-none font-semibold text-[var(--text-bold)] tabular-nums">{c.annees_experience || 0}</p>
            <p className="text-[13px] text-[var(--text-main)] leading-snug">
              an{c.annees_experience > 1 ? 's' : ''} d’expérience<br />
              <span className="text-[var(--text-muted)]">déclarée{c.annees_experience > 1 ? 's' : ''} par le candidat</span>
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Info icone={Briefcase} libelle="Profil">{c.profil_libelle}</Info>
            <Info icone={Mail} libelle="E-mail"><a href={`mailto:${c.email}`} className="hover:underline">{c.email}</a></Info>
            <Info icone={MapPin} libelle="Localisation">{[c.ville, c.pays].filter(Boolean).join(', ')}</Info>
            <Info icone={Building2} libelle="Organisation actuelle">{c.organisation}</Info>
            {contacts.length > 0 && (
              <div className="sm:col-span-2"><Info icone={Phone} libelle={contacts.length > 1 ? 'Téléphones' : 'Téléphone'}>
                <ul className="space-y-1">
                  {contacts.map((t) => (
                    <li key={t.numero} className="flex items-center gap-2 flex-wrap">
                      <a href={`tel:${t.numero.replace(/\s/g, '')}`} className="hover:underline tabular-nums">{t.numero}</a>
                      {t.whatsapp && lienWhatsApp(t.numero) && (
                        <a href={lienWhatsApp(t.numero)} target="_blank" rel="noreferrer" className="etiquette etiq-ok text-[11px]">WhatsApp</a>
                      )}
                    </li>
                  ))}
                </ul>
              </Info></div>
            )}
          </div>
        </div>
        <div className="p-6 border-t md:border-t-0 border-[var(--border)]">
          <Titre>Motivation</Titre>
          <p className="text-[14px] text-[var(--text-main)] leading-relaxed whitespace-pre-line">
            {c.motivation || <span className="text-[var(--text-muted)]">Non renseignée.</span>}
          </p>
          {c.resume_expertise && (
            <>
              <div className="h-px bg-[var(--border)] my-5" />
              <Titre>Expertise en bref</Titre>
              <p className="text-[14px] text-[var(--text-main)] leading-relaxed whitespace-pre-line">{c.resume_expertise}</p>
            </>
          )}
        </div>
      </section>

      {/* ── Bandeau : les domaines, un par pastille ───────────────────── */}
      <section className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6">
        <Titre compte={domaines.length}>Domaines d’expertise</Titre>
        {domaines.length === 0 ? (
          <p className="text-[13px] text-[var(--text-muted)]">Aucun domaine déclaré.</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {domaines.map((d) => (
              <li key={d.code} className="inline-flex items-center gap-2 rounded-controle border border-[var(--border)] bg-[var(--bg-main)] px-3.5 py-2 text-[13px] font-semibold text-[var(--text-bold)]">
                <CheckCircle2 className="w-4 h-4 text-[var(--accent-texte)]" />{d.libelle}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Le CV | le grade, la décision, les messages ───────────────── */}
      <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-5 items-start">
        <VueCv candidature={c} />
        <div className="space-y-5 lg:sticky lg:top-4">
          <Grade candidature={c} />
          <Decision candidature={c} peutDecider={peutDecider} onMaj={setCandidature} />
          <Messages candidature={c} peutEcrire={peutDecider} onMaj={setCandidature} />
          <Notes candidature={c} onMaj={setCandidature} />
        </div>
      </div>
    </div>
  );
}
